import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getAuthStateV1 } from "api/UserAPI";

type UserRole = {
  id: string;
  name: string;
};

type School = {
  id: number;
  name: string;
  contact_person?: string | null;
  contact_phone?: string | null;
  domain?: string | null;
  timezone?: string | null;
};

type AuthStateResponse = {
  id: number;
  role: string;
  userRole?: UserRole;
  lastname?: string | null;
  firstname?: string | null;
  middlename?: string | null;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
  logo?: string | null;
  school?: School[] | null;
  school_name?: string | null;
  group?: string | null;
  comboGroup?: string | null;
  status?: number | null;
  date_exam?: string | number | null;
  school_exam_date?: string | number | null;
  instructor_name?: string | null;
  instructor_phone?: string | null;
};

const Card = styled.div`
    margin: 16px 0 24px;
    padding: 16px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Title = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 12px;
`;

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 6px 0;
`;

const Label = styled.div`
    width: 260px;
    color: #6b7280;
`;

const Value = styled.div`
    color: #111827;
`;

const formatDate = (value: string | number | null | undefined) => {
  if (!value) return "ожидает назначения";
  try {
    const num = typeof value === "number" ? value : Number(value);
    const date = Number.isFinite(num)
      ? new Date(num < 2_000_000_000 ? num * 1000 : num)
      : new Date(String(value));
    if (isNaN(date.getTime())) return "ожидает назначения";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear());
    return `${dd}.${mm}.${yyyy}`;
  } catch (_) {
    return "ожидает назначения";
  }
};

const safeValue = (val?: string | null) => (val && String(val).trim() ? String(val) : "ожидает назначения");

const AuthStateCard: React.FC = () => {
  const [data, setData] = useState<AuthStateResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await getAuthStateV1();
        if (!isMounted) return;
        setData(res.data as AuthStateResponse);
      } catch (e) {
        if (!isMounted) return;
        setError("Не удалось получить данные профиля");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);
  
  if (loading) return null;
  if (error) return null;
  
  const schoolName = data?.school_name || (data?.school && data.school.length > 0 ? data.school[0].name : null);
  const gibddCodeRaw = (data as any)?.gibdd_code as string | number | null;
  const groupGAI = gibddCodeRaw == null || String(gibddCodeRaw).trim() === "" ? "ожидает назначения" : String(gibddCodeRaw);
  
  const schoolCodeRaw = (data as any)?.school_code as string | number | null;
  const shouldShowCard = (() => {
    if (schoolCodeRaw == null || String(schoolCodeRaw).trim() === "") return false;
    const codeStr = String(schoolCodeRaw);
    return codeStr === "809" || codeStr === "895";
  })();
  
  if (!shouldShowCard) return null;
  
  const schoolExamDate = formatDate(data?.school_exam_date ?? null);
  const gaiExamDate = formatDate(data?.date_exam ?? null);
  const instructorName = safeValue(data?.instructor_name ?? null);
  const instructorPhone = safeValue(data?.instructor_phone ?? null);

  return (
    <Card>
      {schoolName && <Title>{schoolName}</Title>} <Row> <Label>Группа ГАИ:</Label> <Value>{groupGAI}</Value> </Row>
      <Row> <Label>Дата экзамена в автошколе:</Label> <Value>{schoolExamDate}</Value> </Row> <Row> <Label>Дата экзамена
      ГАИ:</Label> <Value>{gaiExamDate}</Value> </Row> <Row>
        <Label>Ваш инструктор:</Label>
        <Value>{instructorName}</Value>
      </Row>
      <Row>
        <Label>Тел инструктора:</Label>
        <Value>{instructorPhone}</Value>
      </Row>
    </Card>
  );
};

export default AuthStateCard;